import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronRight, Download } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import ComposicaoDaLinha from './ComposicaoDaLinha';
import PassosDaFolha from './PassosDaFolha';
import { usePode } from '@/stores/authStore';
import { useConferencia, useFolha } from './useFolha';
import { capitalizar, competenciaPorExtenso, reais, type Lancamento } from './tipos';

/**
 * O resultado do mês: as linhas que vão para a contabilidade do ERP, na ordem
 * em que o cliente já importa hoje — inclusive as zeradas, porque é assim que
 * o modelo dele sobe.
 */
export default function LancamentosPage() {
  const { folhaId, data: folha, isLoading } = useFolha();
  // Gerar a planilha é permissão à parte: sem ela o botão daria 403 ao clicar.
  const podeExportar = usePode('folha.exportar');
  const { data: conferencia } = useConferencia(folhaId, Boolean(folha));
  const [abertas, setAbertas] = useState<Set<number>>(new Set());

  const alternar = (id: number) =>
    setAbertas((atual) => {
      const proximo = new Set(atual);

      if (!proximo.delete(id)) {
        proximo.add(id);
      }

      return proximo;
    });

  const lancamentos = useQuery({
    queryKey: ['folha-lancamentos', folhaId],
    queryFn: async () => (await api.get<{ data: Lancamento[]; total: number }>(`/folhas/${folhaId}/lancamentos`)).data,
    enabled: Boolean(folhaId),
  });

  const baixar = useMutation({
    mutationFn: async () => {
      const resposta = await api.get(`/folhas/${folhaId}/planilha`, { responseType: 'blob' });
      const url = URL.createObjectURL(resposta.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `IMPORTACAO FOLHA - ${folha?.competencia.slice(5, 7)}-${folha?.competencia.slice(0, 4)}.xls`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (!folha) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível abrir esta competência. Ela pode ter sido excluída.</p>;
  }

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Planilha para o ERP</h1>
          <p className="mt-xs text-[13px] text-muted">
            {capitalizar(competenciaPorExtenso(folha.competencia))} · {lancamentos.data?.data.length ?? 0} lançamentos
          </p>
        </div>

        {podeExportar && (
          <button
            type="button"
            onClick={() => baixar.mutate()}
            disabled={baixar.isPending}
            className="flex h-10 items-center gap-sm rounded-lg bg-accent px-md text-[14px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Download size={16} />
            Baixar a planilha do Compumate
          </button>
        )}
      </header>

      <PassosDaFolha folha={folha} conferencia={conferencia} atual="planilha" />

      {baixar.isError && (
        <p className="mb-md rounded-lg bg-danger/10 px-md py-sm text-[13px] text-danger">
          A planilha só sai com os vínculos resolvidos. Veja a conferência.
        </p>
      )}

      {lancamentos.isLoading && (
        <div className="flex flex-col items-center gap-sm py-2xl text-[13px] text-muted">
          <Preloader />
          Carregando os lançamentos…
        </div>
      )}

      {lancamentos.isSuccess && lancamentos.data.data.length > 0 && (
        <p className="mb-sm text-[13px] text-muted">Clique numa linha para ver de que rubricas saiu o valor.</p>
      )}

      {lancamentos.isSuccess && lancamentos.data.data.length === 0 && (
        <p className="rounded-lg border border-dashed border-border bg-surface px-lg py-lg text-center text-[13px] text-muted">
          Nenhum lançamento nesta competência. Eles são calculados quando a folha é importada.
        </p>
      )}

      {lancamentos.isSuccess && lancamentos.data.data.length > 0 && (
      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-[13px]">
          <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
            <tr>
              <th className="px-sm py-sm text-right font-bold">#</th>
              <th className="px-sm py-sm text-left font-bold">Histórico</th>
              <th className="px-sm py-sm text-left font-bold">Centro de custo</th>
              <th className="px-sm py-sm text-right font-bold">Débito</th>
              <th className="px-sm py-sm text-right font-bold">Crédito</th>
              <th className="px-sm py-sm text-right font-bold">Valor</th>
            </tr>
          </thead>
          <tbody>
            {(lancamentos.data?.data ?? []).map((lancamento) => {
              const aberta = abertas.has(lancamento.id);

              return (
                <Fragment key={lancamento.id}>
                  <tr
                    onClick={() => alternar(lancamento.id)}
                    className={`cursor-pointer border-b border-border last:border-0 hover:bg-accent/5 ${
                      lancamento.origem === 'nova' ? 'bg-warning/10' : aberta ? 'bg-accent/5' : ''
                    }`}
                  >
                    <td className="px-sm py-xs text-right text-muted tabular-nums">{lancamento.ordem}</td>
                    <td className="px-sm py-xs text-ink">
                      <button
                        type="button"
                        aria-expanded={aberta}
                        title={aberta ? 'Fechar a conta' : 'Ver de que rubricas saiu o valor'}
                        onClick={(evento) => {
                          evento.stopPropagation();
                          alternar(lancamento.id);
                        }}
                        className="flex items-center gap-xs text-left"
                      >
                        <ChevronRight size={14} className={`shrink-0 text-muted transition-transform ${aberta ? 'rotate-90' : ''}`} />
                        {lancamento.historico}
                      </button>
                    </td>
                    <td className="px-sm py-xs text-muted">{lancamento.centro_custo}</td>
                    <td className="px-sm py-xs text-right tabular-nums">{lancamento.conta_debito}</td>
                    <td className="px-sm py-xs text-right tabular-nums">{lancamento.conta_credito}</td>
                    <td className={`px-sm py-xs text-right tabular-nums ${lancamento.valor === 0 ? 'text-muted' : 'font-bold text-ink'}`}>
                      {reais.format(lancamento.valor)}
                    </td>
                  </tr>
                  {aberta && folhaId && (
                    <tr className="border-b border-border bg-accent/5">
                      <td />
                      <td colSpan={5} className="px-sm pb-sm pl-[30px]">
                        <ComposicaoDaLinha folhaId={Number(folhaId)} lancamentoId={lancamento.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
